import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { apiClient } from '../../api/client';
import { formatCurrency } from '../../utils/format';
import { hapticFeedback } from '../../utils/telegram';
import { PaymentMethod } from '../../types';
import { getCategoryIconConfig } from '../../utils/categoryIcons';
import { getAllCategoryIcons, categoryIconsEventEmitter, CATEGORY_ICONS_CHANGED_EVENT } from '../../utils/categoryIconStorage';
import { ReminderSelector } from '../AddTransactionFlow/ReminderSelector';
import './QuickAddTransaction.css';

interface QuickAddTransactionProps {
  type: 'income' | 'expense';
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface FrequentTitle {
  title: string;
  category: string;
  count: number;
}

const FREQUENCY_KEYS: Record<string, string> = {
  onetime: 'transaction.frequency.onetime',
  daily: 'transaction.frequency.daily',
  weekly: 'transaction.frequency.weekly',
  monthly: 'transaction.frequency.monthly',
  yearly: 'transaction.frequency.yearly',
};

export const QuickAddTransaction: React.FC<QuickAddTransactionProps> = ({
  type,
  currency,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [currentType, setCurrentType] = useState<'expense' | 'income'>(type);

  useEffect(() => {
    setCurrentType(type);
  }, [type]);

  const [amount, setAmount] = useState('');
  const [selectedTitle, setSelectedTitle] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [frequency, setFrequency] = useState('onetime');
  const [firstPaymentDateTime, setFirstPaymentDateTime] = useState(() => {
    // По умолчанию текущая дата и время
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });
  const [frequentTitles, setFrequentTitles] = useState<FrequentTitle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [categories, setCategories] = useState<Array<{name: string; emoji?: string}>>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [aiSuggestedCategory, setAiSuggestedCategory] = useState<string>('');
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>(undefined);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [categoryIcons, setCategoryIcons] = useState<Record<string, string>>({});
  const [showReminderSelector, setShowReminderSelector] = useState(false);
  const [reminderTimes, setReminderTimes] = useState<string[]>([]);
  const [reminderOffsets, setReminderOffsets] = useState<number[]>([0]);
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const categoryInputRef = useRef<HTMLSelectElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadFrequentTitles();
    loadCategories();
    loadPaymentMethods();
    loadCategoryIcons();
    
    // Подписываемся на изменения иконок
    const handleIconsChanged = (newIcons: Record<string, string>) => {
      setCategoryIcons(newIcons);
    };
    
    categoryIconsEventEmitter.on(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);
    
    // Автофокус на поле ввода суммы при открытии модалки
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
    
    // Если тип транзакции расход и выбрана частота daily, сбрасываем на weekly
    if (currentType === 'expense' && frequency === 'daily') {
      setFrequency('weekly');
    }

    // Блокируем скролл body когда модалка открыта
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      categoryIconsEventEmitter.off(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);
    };
  }, [type, currentType]);

  const loadCategoryIcons = () => {
    const icons = getAllCategoryIcons();
    setCategoryIcons(icons);
  };

  // Определение категории через AI при вводе названия для регулярных расходов
  useEffect(() => {
    const detectCategoryWithAI = async () => {
      // Только для регулярных расходов, если нет категории и есть название
      if (currentType === 'expense' && frequency !== 'onetime' && selectedTitle && !selectedCategory && Array.isArray(categories) && categories.length > 0) {
        setIsLoadingAI(true);
        try {
          const result = await apiClient.suggestCategory(selectedTitle);
          if (result.success && result.category) {
            setAiSuggestedCategory(result.category);
            // Автоматически устанавливаем предложенную категорию
            setSelectedCategory(result.category);
          }
        } catch (error) {
          console.error('Error detecting category with AI:', error);
        } finally {
          setIsLoadingAI(false);
        }
      } else if (!selectedTitle || frequency === 'onetime') {
        setAiSuggestedCategory('');
      }
    };

    // Добавляем задержку, чтобы не вызывать AI при каждом символе
    const timeoutId = setTimeout(() => {
      detectCategoryWithAI();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [selectedTitle, frequency, currentType, selectedCategory, categories]);

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const response = await apiClient.getCategories();
      // API возвращает объект { categories: Category[] }
      const cats = response?.categories || response;
      // Убеждаемся, что это массив
      if (Array.isArray(cats)) {
        setCategories(cats);
      } else {
        console.error('getCategories returned non-array:', response);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await apiClient.getPaymentMethods();
      const methods = response.payment_methods || [];
      
      // Сортируем: счет по умолчанию первым
      const sortedMethods = [...methods].sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return 0;
      });
      
      setPaymentMethods(sortedMethods);
      
      // Автоматически выбираем счет по умолчанию
      if (sortedMethods.length > 0) {
        const defaultMethod = sortedMethods.find(m => m.is_default);
        if (defaultMethod) {
          setSelectedPaymentMethodId(defaultMethod.id);
        } else {
          // Если нет счета по умолчанию, выбираем первый
          setSelectedPaymentMethodId(sortedMethods[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Обработка появления клавиатуры для прокрутки к активному полю
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target === titleInputRef.current || target === dateInputRef.current)) {
        // Ждем немного, чтобы клавиатура успела появиться
        setTimeout(() => {
          // Прокручиваем модальное окно, чтобы поле было видно
          if (modalContentRef.current) {
            const rect = target.getBoundingClientRect();
            const modalRect = modalContentRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            
            // Если поле находится под клавиатурой (в нижней части экрана)
            if (rect.bottom > viewportHeight - 200) {
              const relativeTop = rect.top - modalRect.top + modalContentRef.current.scrollTop;
              modalContentRef.current.scrollTo({
                top: relativeTop - 100, // Отступ сверху для видимости
                behavior: 'smooth'
              });
            }
          }
        }, 300);
      }
    };

    const titleInput = titleInputRef.current;
    const dateInput = dateInputRef.current;

    if (titleInput) {
      titleInput.addEventListener('focus', handleFocus);
    }
    if (dateInput) {
      dateInput.addEventListener('focus', handleFocus);
    }

    return () => {
      if (titleInput) {
        titleInput.removeEventListener('focus', handleFocus);
      }
      if (dateInput) {
        dateInput.removeEventListener('focus', handleFocus);
      }
    };
  }, [frequency]);

  const loadFrequentTitles = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getFrequentTitles(currentType);
      const titles = response.frequent_titles || [];
      
      // Добавляем дефолтную категорию в начало, если её ещё нет
      const defaultTitle = currentType === 'income' ? t('transaction.income') : t('transaction.expense');
      const hasDefault = titles.some(t => t.title === defaultTitle);
      
      if (!hasDefault) {
        titles.unshift({
          title: defaultTitle,
          category: currentType === 'income' ? 'income' : 'expense',
          count: 0
        });
      }
      
      setFrequentTitles(titles);
    } catch (error) {
      console.error('Error loading frequent titles:', error);
      // Даже при ошибке показываем дефолтную категорию
      const defaultTitle = currentType === 'income' ? t('transaction.income') : t('transaction.expense');
      setFrequentTitles([{
        title: defaultTitle,
        category: currentType === 'income' ? 'income' : 'expense',
        count: 0
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Разрешаем только цифры и точку
    if (/^\d*\.?\d*$/.test(value) || value === '') {
      setAmount(value);
    }
  };

  const handleTitleClick = (title: FrequentTitle) => {
    hapticFeedback.medium();
    setSelectedTitle(title.title);
    setSelectedCategory(title.category);
  };

  const handleCategoryClick = (categoryName: string) => {
    hapticFeedback.medium();
    setSelectedTitle(categoryName);
    setSelectedCategory(categoryName);
  };

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert(t('transaction.enterAmount'));
      return;
    }

    // Для разовых транзакций: если название не заполнено, используем название категории
    const finalTitle = frequency === 'onetime' 
      ? (customTitle.trim() || selectedCategory || selectedTitle || '')
      : selectedTitle;

    if (!finalTitle && frequency !== 'onetime') {
      alert(t('transaction.selectTitle'));
      return;
    }

    if (!finalTitle && frequency === 'onetime' && !selectedCategory) {
      alert(t('transaction.selectCategoryOrTitle'));
      return;
    }

    try {
      setIsSaving(true);
      hapticFeedback.success();

      const amountValue = parseFloat(amount);

      if (frequency !== 'onetime') {
        // Для регулярных транзакций
        if (currentType === 'income') {
          // Регулярный доход
          const formatDateLocal = (dateTimeStr: string): string => {
            const date = new Date(dateTimeStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          const formatTimeLocal = (dateTimeStr: string): string => {
            const date = new Date(dateTimeStr);
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
          };

          const recurringIncomeData = {
            title: finalTitle,
            default_amount: amountValue,
            frequency: frequency as 'daily' | 'weekly' | 'monthly' | 'yearly',
            start_date: formatDateLocal(firstPaymentDateTime),
            notification_time: formatTimeLocal(firstPaymentDateTime),
            reminder_times: remindersEnabled ? reminderTimes : [],
            reminder_offsets: remindersEnabled ? reminderOffsets : [0],
            reminders_enabled: remindersEnabled,
            description: '',
          };

          await apiClient.addRecurringIncomeDefinition(recurringIncomeData);
        } else {
          // Регулярный расход
          // Проверяем поддерживаемые типы (daily не поддерживается для расходов)
          if (frequency === 'daily') {
            alert(t('transaction.dailyNotSupported'));
            setIsSaving(false);
            return;
          }

          const formatDateLocal = (dateTimeStr: string): string => {
            const date = new Date(dateTimeStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          const formatTimeLocal = (dateTimeStr: string): string => {
            const date = new Date(dateTimeStr);
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
          };

          const recurringExpenseData = {
            title: finalTitle,
            amount: amountValue,
            category: selectedCategory || undefined,
            expense_type: frequency as 'weekly' | 'monthly' | 'yearly',
            start_date_recurring: formatDateLocal(firstPaymentDateTime),
            notification_time: formatTimeLocal(firstPaymentDateTime),
            disable_payment_notifications: false,
            payment_method_id: selectedPaymentMethodId,
            goal_id: undefined,
            reminder_times: remindersEnabled ? reminderTimes : [],
            reminder_offsets: remindersEnabled ? reminderOffsets : [0],
            reminders_enabled: remindersEnabled,
            description: '',
          };

          await apiClient.addRecurringExpense(recurringExpenseData);
        }
      } else {
        // Для разовых транзакций
        const transactionData: any = {
          type: currentType,
          amount: amountValue,
          title: finalTitle,
          category: selectedCategory,
          date: new Date().toISOString(),
        };

        // Добавляем счет, если он выбран
        if (selectedPaymentMethodId) {
          transactionData.payment_method_id = selectedPaymentMethodId;
        }

        await apiClient.addTransaction(transactionData);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving transaction:', error);
      alert(error.message || t('transaction.saveError'));
      hapticFeedback.error();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="quick-add-overlay" onClick={onClose}>
      <div className="quick-add-modal" onClick={(e) => e.stopPropagation()} ref={modalContentRef}>
        {/* Header: type toggle + Close + Save */}
        <div className="quick-add-header">
          <div className="quick-add-type-toggle">
            <button
              type="button"
              className={`quick-add-type-btn ${currentType === 'expense' ? 'quick-add-type-btn--active quick-add-type-btn--expense' : ''}`}
              onClick={() => { setCurrentType('expense'); hapticFeedback.selection(); }}
            >
              <Icon name="arrow-down" size={16} color={currentType === 'expense' ? '#FFFFFF' : 'var(--color-text-secondary)'} />
              <span>{t('quickActions.expense')}</span>
            </button>
            <button
              type="button"
              className={`quick-add-type-btn ${currentType === 'income' ? 'quick-add-type-btn--active quick-add-type-btn--income' : ''}`}
              onClick={() => { setCurrentType('income'); hapticFeedback.selection(); }}
            >
              <Icon name="arrow-up" size={16} color={currentType === 'income' ? '#FFFFFF' : 'var(--color-text-secondary)'} />
              <span>{t('quickActions.income')}</span>
            </button>
          </div>
          <div className="quick-add-header-actions">
            <button className="quick-add-close" onClick={onClose} aria-label={t('common.close')}>
              <Icon name="close" size={24} />
            </button>
            <button
              className="quick-add-save-btn"
              onClick={handleSave}
              disabled={!amount || parseFloat(amount) <= 0 || isSaving || (frequency === 'onetime' && !customTitle.trim() && !selectedCategory && !selectedTitle)}
            >
              {isSaving ? '...' : t('common.save')}
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="quick-add-scroll-content">
          {/* Frequency Selector and Payment Method - в одной строке */}
          <div className="quick-add-frequency-payment-row">
            {/* Тип - слева */}
            <div className="quick-add-frequency-container">
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                marginBottom: '6px',
                display: 'block'
              }}>
                {t('transaction.periodicity')}:
              </label>
              <select 
                value={frequency} 
                onChange={(e) => {
                  setFrequency(e.target.value);
                  hapticFeedback.selection();
                  // Сбрасываем категорию при смене частоты на разовую
                  if (e.target.value === 'onetime') {
                    setSelectedCategory('');
                    setAiSuggestedCategory('');
                  }
                }}
                className="quick-add-frequency-select neo-inset"
                style={{
                  width: '100%',
                  padding: '8px 40px 8px 12px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--neo-bg)',
                  color: 'var(--color-text-primary)',
                  minHeight: '36px',
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                }}
              >
                {(Object.entries(FREQUENCY_KEYS) as [string, string][])
                  .filter(([value]) => {
                    if (currentType === 'expense' && value === 'daily') return false;
                    return true;
                  })
                  .map(([value, key]) => (
                    <option key={value} value={value}>
                      {t(key)}
                    </option>
                  ))}
              </select>
            </div>

            {/* Счет - справа */}
            {paymentMethods.length > 0 && (
              <div className="quick-add-payment-container">
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '6px',
                  display: 'block'
                }}>
                  {t('transaction.account')}:
                </label>
                {loadingPaymentMethods ? (
                  <div className="quick-add-loading">{t('common.loading')}</div>
                ) : (
                  <select
                    value={selectedPaymentMethodId || ''}
                    onChange={(e) => {
                      setSelectedPaymentMethodId(e.target.value || undefined);
                      hapticFeedback.selection();
                    }}
                    className="neo-inset"
                    style={{
                      width: '100%',
                      padding: '8px 40px 8px 12px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--neo-bg)',
                      color: 'var(--color-text-primary)',
                      minHeight: '36px',
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                    }}
                    disabled={loadingPaymentMethods || isSaving}
                  >
                    <option value="">{t('transaction.selectAccount')}</option>
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.name}{method.is_default ? ` (${t('transaction.default')})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Поля для регулярных транзакций */}
          {frequency !== 'onetime' && (
            <div className="quick-add-recurring-fields">
              {/* Поле ввода названия регулярной транзакции */}
              <div className="quick-add-input-container">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={selectedTitle}
                  onChange={(e) => {
                    setSelectedTitle(e.target.value);
                    hapticFeedback.selection();
                  }}
                  placeholder={currentType === 'income' ? t('transaction.recurringIncomePlaceholder') : t('transaction.recurringExpensePlaceholder')}
                  className="neo-inset"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--neo-bg)',
                    color: 'var(--color-text-primary)',
                    minHeight: '36px'
                  }}
                />
              </div>

              {/* Выбор категории для регулярных расходов - горизонтальный скролл */}
              {currentType === 'expense' && (
                <div className="quick-add-category-wrapper">
                  <label style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--color-text-secondary)',
                    marginBottom: '6px',
                    display: 'block'
                  }}>
                    {t('transaction.category')}: {isLoadingAI && <span style={{ fontSize: '12px', color: 'var(--color-primary)' }}>({t('transaction.aiDetecting')})</span>}
                  </label>
                  {isLoadingCategories ? (
                    <div className="quick-add-loading">{t('common.loading')}</div>
                  ) : (
                    <>
                      <div style={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: '8px',
                        paddingBottom: '4px',
                        WebkitOverflowScrolling: 'touch'
                      }}>
                        {categories
                          .filter(cat => cat && cat.name && !cat.name.includes('income') && cat.name !== t('transaction.income'))
                          .map((cat, index) => {
                            const customIconName = categoryIcons[cat.name] || cat.emoji || null;
                            const iconConfig = getCategoryIconConfig(cat.name, customIconName);
                            const isSelected = selectedCategory === cat.name;
                            
                            return (
                              <button
                                key={`${cat.name}-${index}`}
                                onClick={() => {
                                  setSelectedCategory(cat.name);
                                  hapticFeedback.selection();
                                }}
                                disabled={isSaving || isLoadingAI}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '8px 12px',
                                  borderRadius: '12px',
                                  border: 'none',
                                  backgroundColor: isSelected ? iconConfig.color : 'var(--neo-bg)',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  minWidth: '80px',
                                  flexShrink: 0
                                }}
                                className={isSelected ? 'neo-inset' : 'neo-raised'}
                              >
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : iconConfig.backgroundColor,
                                  marginBottom: '4px'
                                }}>
                                  <Icon
                                    name={iconConfig.icon}
                                    size={20}
                                    color={isSelected ? '#FFFFFF' : iconConfig.color}
                                  />
                                </div>
                                <span style={{
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  color: isSelected ? '#FFFFFF' : 'var(--color-text-primary)',
                                  textAlign: 'center',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%'
                                }}>
                                  {cat.name}
                                </span>
                              </button>
                            );
                          })}
                      </div>
                      {aiSuggestedCategory && selectedCategory === aiSuggestedCategory && !isLoadingAI && (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '12px',
                          color: 'var(--color-primary)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: '600',
                          backgroundColor: 'rgba(175, 82, 222, 0.1)',
                          padding: '4px 8px',
                          borderRadius: '8px',
                          width: 'fit-content'
                        }}>
                          <Icon name="lightbulb" size={14} />
                          <span>{t('transaction.aiSuggested')}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Поле даты и времени первого платежа */}
              <div className="quick-add-input-container">
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '6px',
                  display: 'block'
                }}>
                  {t('transaction.dateTime')}
                </label>
                <input
                  ref={dateInputRef}
                  type="datetime-local"
                  value={firstPaymentDateTime}
                  onChange={(e) => {
                    setFirstPaymentDateTime(e.target.value);
                    hapticFeedback.selection();
                  }}
                  className="neo-inset"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--neo-bg)',
                    color: 'var(--color-text-primary)',
                    minHeight: '36px'
                  }}
                />
              </div>

              {/* Напоминания */}
              <div className="quick-add-input-container">
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  <div>
                    <label style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--color-text-secondary)',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      {t('recurring.reminders')}
                    </label>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      opacity: 0.8
                    }}>
                      {remindersEnabled 
                        ? `${reminderOffsets.length} дней × ${reminderTimes.length} времени = ${reminderOffsets.length * reminderTimes.length} напоминаний`
                        : t('recurring.notIncluded')}
                    </div>
                  </div>
                  <button
                    onClick={() => setRemindersEnabled(!remindersEnabled)}
                    disabled={isSaving}
                    style={{
                      width: '44px',
                      height: '24px',
                      borderRadius: '12px',
                      border: 'none',
                      background: remindersEnabled ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s',
                      padding: '2px'
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      transform: remindersEnabled ? 'translateX(20px)' : 'translateX(0)',
                      transition: 'transform 0.2s'
                    }} />
                  </button>
                </div>
                {remindersEnabled && (
                  <button
                    onClick={() => setShowReminderSelector(true)}
                    disabled={isSaving}
                    className="neo-raised"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--neo-bg)',
                      color: 'var(--color-text-primary)',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>Настроить напоминания</span>
                    <Icon name="chevron-right" size={20} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Amount Input */}
          <div className="quick-add-amount-container">
            <div className="quick-add-amount-display neo-inset">
              <input
                ref={inputRef}
                type="number"
                inputMode="decimal"
                step="any"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0"
                className="quick-add-amount-input"
                disabled={isSaving}
                autoFocus
              />
              <span className="quick-add-currency">{currency}</span>
            </div>
          </div>

          {/* Categories (только для разовых транзакций) */}
          {frequency === 'onetime' && (
            <div className="quick-add-categories-section">
              {/* Поле для ввода названия */}
              <div className="quick-add-input-container">
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '6px',
                  display: 'block'
                }}>
                  {t('transaction.titleOptional')}:
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder={selectedCategory ? `${t('transaction.default')}: ${selectedCategory}` : t('transaction.selectCategoryOrTitle')}
                  className="neo-inset"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--neo-bg)',
                    color: 'var(--color-text-primary)',
                    minHeight: '36px'
                  }}
                  disabled={isSaving}
                />
              </div>

              {/* Горизонтальный скролл категорий с иконками */}
              <div className="quick-add-category-wrapper">
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '6px',
                  display: 'block'
                }}>
                  {t('transaction.category')}:
                </label>
                {isLoadingCategories ? (
                  <div className="quick-add-loading">{t('common.loading')}</div>
                ) : (
                  <div style={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: '8px',
                    paddingBottom: '4px',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    {(() => {
                      // Создаем Map частоты из frequentTitles
                      const frequencyMap = new Map(
                        frequentTitles.map(item => [item.title, item.count])
                      );
                      
                      // Объединяем все категории с учетом популярности
                      const allCategories = categories
                        .filter(cat => !cat.name?.toLowerCase().includes('system'))
                        .map(cat => ({
                          name: cat.name,
                          emoji: cat.emoji,
                          count: frequencyMap.get(cat.name) || 0
                        }))
                        // Сортируем по популярности (count) по убыванию
                        .sort((a, b) => b.count - a.count);
                      
                      return allCategories.map((cat, index) => {
                        // Получаем кастомную иконку из сохраненных или из emoji поля категории
                        const customIconName = categoryIcons[cat.name] || cat.emoji || null;
                        const iconConfig = getCategoryIconConfig(cat.name, customIconName);
                        const isSelected = selectedCategory === cat.name;
                        
                        return (
                          <button
                            key={`${cat.name}-${index}`}
                            onClick={() => handleCategoryClick(cat.name)}
                            disabled={isSaving}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '8px 12px',
                              borderRadius: '12px',
                              border: 'none',
                              backgroundColor: isSelected ? iconConfig.color : 'var(--neo-bg)',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              minWidth: '80px',
                              flexShrink: 0
                            }}
                            className={isSelected ? 'neo-inset' : 'neo-raised'}
                          >
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : iconConfig.backgroundColor,
                              marginBottom: '4px'
                            }}>
                              <Icon
                                name={iconConfig.icon}
                                size={20}
                                color={isSelected ? '#FFFFFF' : iconConfig.color}
                              />
                            </div>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: '500',
                              color: isSelected ? '#FFFFFF' : 'var(--color-text-primary)',
                              textAlign: 'center',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}>
                              {cat.name}
                            </span>
                          </button>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="quick-add-footer">
          <button 
            className="quick-add-button quick-add-button--cancel neo-raised" 
            onClick={onClose}
            disabled={isSaving}
          >
            {t('common.cancel')}
          </button>
          <button 
            className="quick-add-button quick-add-button--save neo-raised" 
            onClick={handleSave}
            disabled={isSaving || !amount || (frequency === 'onetime' && !customTitle.trim() && !selectedCategory && !selectedTitle) || (frequency !== 'onetime' && !selectedTitle)}
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Reminder Selector Modal */}
      {showReminderSelector && (
        <div className="quick-add-overlay" style={{ zIndex: 10001 }} onClick={() => setShowReminderSelector(false)}>
          <div className="quick-add-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <ReminderSelector
              reminderTimes={reminderTimes}
              reminderOffsets={reminderOffsets}
              remindersEnabled={remindersEnabled}
              transactionType={currentType}
              onChange={(data) => {
                setReminderTimes(data.reminderTimes || []);
                setReminderOffsets(data.reminderOffsets || [0]);
                setRemindersEnabled(data.remindersEnabled || false);
                setShowReminderSelector(false);
              }}
              onBack={() => setShowReminderSelector(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

